import { Component, computed, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IconModule } from '@insolution/core-data-access';
import { bsnValidator, getDateYearsFromNow, isInPastValidator, isValidDateValidator } from '@insolution/core-utils';
import { CountrySelector } from '@insolution/countries-feature';
import {
  Patient,
  EPatientGender,
  EPatientIdType,
  EPatientStatus,
  EPatientTitle,
} from '@insolution/graphql-data-access';
import { MultiLanguageDatepicker } from '@insolution/language-feature';
import { TranslatePipe } from '@ngx-translate/core';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { PatientFormGroupControls } from './interfaces/patient-form.interface';

@Component({
  selector: 'lib-patient-form',
  imports: [ReactiveFormsModule, TranslatePipe, MultiLanguageDatepicker, CountrySelector, IconModule],
  templateUrl: './patient-form.html',
  styleUrl: './patient-form.scss',
})
export class PatientForm {
  public isLoading = model(false);
  public errorMessage = model<string | null>(null);
  public patient = input<Patient | undefined>();
  public patientSubmit = output<Patient>();
  public patientSubmitAndStartConsult = output<Patient>();
  public checkForSimilarPatient = output<Pick<Patient, 'last_name' | 'date_of_birth'>>();
  public unsavedChangesExist = model(false);

  private formBuilder = inject(FormBuilder);

  protected submitted = signal(false);

  protected genderOptions = Object.values(EPatientGender);
  protected statusOptions = computed(() => {
    const patient = this.patient();
    if (patient?.status === EPatientStatus.Anonymized) {
      return [EPatientStatus.Anonymized];
    }
    return Object.values(EPatientStatus).filter((status) => status !== EPatientStatus.Anonymized);
  });
  protected isPatientAnonymized = computed(() => this.patient()?.status === EPatientStatus.Anonymized);
  protected titleOptions = Object.values(EPatientTitle);
  protected idTypeOptions = Object.values(EPatientIdType);

  protected maxDateForDateOfBirth = getDateYearsFromNow(0);
  protected minDateForDateOfBirth = getDateYearsFromNow(-120);

  protected formGroup = this.formBuilder.group<PatientFormGroupControls>({
    id: this.formBuilder.control(null),
    initials: this.formBuilder.control(null),
    title: this.formBuilder.control<EPatientTitle | null>(null),
    first_name: this.formBuilder.control(null),
    middle_name: this.formBuilder.control(null),
    last_name: this.formBuilder.control(null, [Validators.required]),
    date_of_birth: this.formBuilder.control(null, [isValidDateValidator(), isInPastValidator(), Validators.required]),
    gender: this.formBuilder.control<EPatientGender | null>(null),
    id_type: this.formBuilder.control<EPatientIdType | null>(null),
    id_number: this.formBuilder.control(null),
    bsn_number: this.formBuilder.control(null, [bsnValidator()]),
    external_id: this.formBuilder.control(null),
    health_insurer: this.formBuilder.control(null),
    health_insurance_number: this.formBuilder.control(null),
    referrer: this.formBuilder.control(null),
    street: this.formBuilder.control(null),
    house_number: this.formBuilder.control(null),
    postal_code: this.formBuilder.control(null),
    province: this.formBuilder.control(null),
    city: this.formBuilder.control(null),
    country: this.formBuilder.control(null),
    phone: this.formBuilder.control(null),
    mobile: this.formBuilder.control(null),
    email: this.formBuilder.control(null, [Validators.email]),
    status: this.formBuilder.control<EPatientStatus | null>(null),
    notes: this.formBuilder.control(null),
  });

  private lastNameSignal = toSignal(this.formGroup.controls.last_name.valueChanges, {
    initialValue: this.formGroup.controls.last_name.value,
  });
  private dateOfBirthSignal = toSignal(this.formGroup.controls.date_of_birth.valueChanges, {
    initialValue: this.formGroup.controls.date_of_birth.value,
  });

  private formIsReady = false;
  private countriesIsReady = false;

  constructor() {
    effect(() => {
      const patient = this.patient();

      untracked(() => {
        this.formIsReady = false;

        if (patient) {
          this.patchPatientForm(patient);
        } else {
          this.formGroup.reset();
          this.formGroup.controls.id.setValue(crypto.randomUUID());
          this.formGroup.controls.status.setValue(EPatientStatus.Active);
          setTimeout(() => (this.formIsReady = true));
        }
      });
    });

    this.formGroup.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => this.formIsReady && this.countriesIsReady),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.submitted.set(false);
        this.errorMessage.set(null);
        this.unsavedChangesExist.set(true);
      });

    effect(() => {
      const lastName = this.lastNameSignal();
      const dateOfBirth = this.dateOfBirthSignal();
      const dateOfBirthIsValid = this.formGroup.controls.date_of_birth.valid;

      if (lastName && dateOfBirth && dateOfBirthIsValid) {
        this.checkForSimilarPatient.emit({
          last_name: lastName,
          date_of_birth: dateOfBirth,
        });
        return;
      }
      this.checkForSimilarPatient.emit({
        last_name: '',
        date_of_birth: '',
      });
    });
  }

  protected onSubmit(startConsult?: boolean) {
    this.submitted.set(true);
    if (this.formGroup.invalid) {
      return;
    }

    const formData = {
      ...this.formGroup.value,
    } as Patient;

    if (this.patient()) {
      formData.tenant_id = this.patient()!.tenant_id;
      formData.tenant_ref = this.patient()!.tenant_ref;
      formData.patient_number = this.patient()!.patient_number;
    }

    if (startConsult) {
      this.patientSubmitAndStartConsult.emit(formData);
    } else {
      this.patientSubmit.emit(formData);
    }
  }

  protected setPatientsCountry() {
    const patient = this.patient();
    if (patient?.country) {
      this.formGroup.controls.country.setValue(patient.country);
    }

    this.countriesIsReady = true;
  }

  private patchPatientForm(patient: Patient) {
    this.formGroup.patchValue({
      ...patient,
    });
    setTimeout(() => (this.formIsReady = true));
  }
}
